import { isNil } from 'lodash-es';
import { type CSSProperties, type PropsWithChildren } from 'react';

import { type MaskingProps } from '@dynatrace/strato-components/core';

import { SparklineContainer } from './SparklineContainer.js';
import type { SparklineProps } from './types/sparkline-props.js';
import type { SparklineThresholdProps } from './types/sparkline-thresholds-props.js';
import { SparklineXAxisInternalProps } from './types/sparkline-x-axis-props.js';
import type { SparklineYAxisProps } from './types/sparkline-y-axis-props.js';
import { defaultChartSize } from '../../../../core/styles/defaultChartValues.sty.js';
import { Timeseries } from '../../types/timeseries.js';
import { ChartStateHandler } from '../chart-states/ChartStateHandler.js';
import { getChartStatesTemplates } from '../chart-states/utils/get-chart-states-template.js';

export interface SparklineDisplayProps extends SparklineProps, MaskingProps {
  thresholds?: SparklineThresholdProps[];
  xAxis: SparklineXAxisInternalProps;
  yAxis: SparklineYAxisProps;
  style?: CSSProperties;
}

/**
 * SparklineDisplay component
 */
export const SparklineDisplay = (
  props: PropsWithChildren<SparklineDisplayProps>,
) => {
  const { data, loading = false, children } = props;

  const series: Timeseries = Array.isArray(data) ? data[0] : data;
  const { emptyState, errorState } = getChartStatesTemplates(children);

  const isEmpty = isNil(series) || series.datapoints?.length === 0;

  return (
    <ChartStateHandler
      data-testid="sparkline-state-handler"
      empty={isEmpty}
      height={'100%'}
      emptyState={emptyState}
      errorState={errorState}
      isLoading={loading}
      className={defaultChartSize}
    >
      <SparklineContainer {...props} data={series} />
    </ChartStateHandler>
  );
};

SparklineDisplay['displayName'] = 'SparklineDisplay';
